/**
 * The desktop build, offered to somebody reading their mail in a browser.
 *
 * # Why this is not on the transport
 *
 * Everything else about updating is: the desktop app replaces itself, which is a
 * capability of the platform and is `null` in a browser — see `UpdateControl` in
 * `lib/net.types.ts`. This is the other direction and is nothing so grand. It is
 * one authenticated GET for a JSON document, which any build can make, and the
 * only reason it is only *drawn* in a browser is that somebody already running
 * the desktop app has no use for a link to it.
 *
 * # Absent, not disabled
 *
 * Serving releases is optional. A daemon that has none answers 404 or 501, an
 * older one has never heard of the path, and a deployment behind a proxy that
 * has not been told about it answers something else again. All of those mean the
 * same thing to a settings screen — there is no download to offer — and the rule
 * this project keeps for that case is that the row is not there at all rather
 * than there and greyed out. A disabled "Download" button is a question nobody
 * on this side can answer.
 *
 * So every failure lands as `null` and none of them as a rejection. That is a
 * deliberate departure from how the rest of this client treats errors, and the
 * justification is narrow: nothing here is something the reader asked for. The
 * row draws itself while they were looking at something else.
 *
 * # The URL expires, and that is fine
 *
 * The daemon rewrites the manifest's relative filename into an absolute,
 * pre-signed URL good for about fifteen minutes — the artifact is then fetched
 * with no credential at all, which is what lets the browser's own download
 * machinery have it. Fifteen minutes is far longer than the gap between a
 * settings page rendering and somebody clicking the link on it, and the fix if
 * it ever is not is to re-open the page. Hence {@link desktopDownloads} is
 * called on render and its answer is not cached anywhere.
 */

import { request } from "@transport";

import { authHeaders } from "./settings";

/**
 * The manifest, as the Tauri updater's own format defines it.
 *
 * The same document the desktop build checks against — one file serving both,
 * rather than a second endpoint that would have to be kept in step. The desktop
 * app reads it through the updater plugin and cares about the signature; this
 * reads it as JSON and cares about the URL.
 */
const MANIFEST = "/releases/desktop/latest.json";

/**
 * The platforms there are desktop builds for, keyed by what a visitor can be on.
 *
 * Named here rather than inlined because these are *facts about what is
 * released*, not about what the format allows: the release script bundles a
 * Linux AppImage and a macOS universal app, and nothing else. Somebody adding a
 * Windows build changes this table and should have to notice they are doing so.
 *
 * The two darwin keys are an ordered preference, not alternatives: the release
 * script writes both, naming the same universal bundle, because the Tauri
 * updater asks for the arch of the running slice and a manifest carrying only
 * one key would strand the other. This page cannot tell an Apple Silicon
 * browser from an Intel one and does not need to — whichever key is present
 * first is the same file.
 */
const OFFERS: Record<VisitorOs, readonly string[]> = {
  linux: ["linux-x86_64"],
  macos: ["darwin-aarch64", "darwin-x86_64"],
};

/** Every platform there is a build for, in the order a stranger is shown them. */
const PLATFORMS: readonly VisitorOs[] = ["linux", "macos"];

/** The two kinds of desktop there is anything to offer. */
export type VisitorOs = "linux" | "macos";

/**
 * What the visitor is on, from the one field every browser still fills in.
 *
 * `navigator.platform` is deprecated in favour of client hints that Firefox and
 * Safari do not implement, and it still answers "MacIntel" on every Mac —
 * including Apple Silicon, which is half of why {@link OFFERS} does not try to
 * pick an arch. Anything unrecognised gets `null`, which is a missing
 * *preference* rather than a missing row: the visitor is shown every build
 * there is, in {@link PLATFORMS} order, because somebody on a machine we do
 * not release for is often fetching for the machine next to it.
 */
export function visitorOs(platform: string): VisitorOs | null {
  if (/Mac/.test(platform)) return "macos";
  if (/Linux/.test(platform)) return "linux";
  return null;
}

/** A build that exists, and where to get it. */
export interface DesktopDownload {
  version: string;
  /** Absolute, pre-signed, and short-lived. See the note at the top of the file. */
  url: string;
  /** Which build this is, so the row can say so — and warn about Gatekeeper. */
  os: VisitorOs;
}

/**
 * What this daemon has for a desktop — every platform of it, or nothing.
 *
 * Every build in the manifest is offered, not just the visitor's own: a person
 * reading mail on one machine and fetching an installer for the one next to it
 * is ordinary, and a page that hid the other build would make them go find the
 * other computer first. The visitor's own platform leads the list; the order is
 * the only privilege it gets.
 *
 * Empty covers every way it can go wrong, including a manifest that parses but
 * names no bundle this project releases — a daemon serving only Windows builds
 * has nothing to offer anybody here.
 */
export async function desktopDownloads(): Promise<DesktopDownload[]> {
  let res: Response;
  try {
    res = await request(MANIFEST, {
      // The cookie is the credential in the browser and it is attached by the
      // browser; `authHeaders` adds the bearer where there is one, which is what
      // stops this being the one bare request in the client. See the note in
      // `lib/settings.ts` for what leaving it out cost the last time.
      credentials: "same-origin",
      headers: authHeaders(),
    });
  } catch {
    // The daemon is unreachable. Whatever screen this is on has bigger news for
    // the reader than a missing download link.
    return [];
  }

  if (!res.ok) return [];

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    // A proxy's HTML error page, served with a 200. Not a release.
    return [];
  }

  return offersIn(body);
}

/**
 * The downloads in a parsed manifest, the visitor's own platform first.
 *
 * Split out and exported so the shape check can be tested without a server, and
 * checked field by field rather than cast: this is JSON from the network being
 * turned into URLs that go into `href`s, and `as DesktopDownload` would make
 * `undefined` into a link reading "undefined".
 */
export function offersIn(body: unknown, os = visitorOs(navigator.platform)): DesktopDownload[] {
  if (typeof body !== "object" || body === null) return [];
  const manifest = body as {
    version?: unknown;
    platforms?: Record<string, { url?: unknown } | undefined>;
  };

  const version = manifest.version;
  if (typeof version !== "string" || version === "") return [];

  // The visitor's platform, then everybody else's. A visitor nobody built for
  // gets the unweighted list — see `visitorOs`.
  const order = os === null ? PLATFORMS : [os, ...PLATFORMS.filter((p) => p !== os)];

  const out: DesktopDownload[] = [];
  for (const platform of order) {
    for (const key of OFFERS[platform]) {
      const url = manifest.platforms?.[key]?.url;
      if (typeof url === "string" && url !== "") {
        out.push({ version, url, os: platform });
        break;
      }
    }
  }
  return out;
}
